'use client';

import { useCallback, useMemo, useRef } from 'react';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useRouter } from '@nx-frontend/shared/hooks/router-proxy';
import { $members } from '@nx-frontend/shared/store/members';
import { $user } from '@nx-frontend/shared/store/user';
import { $dateTimeLocale } from '@nx-frontend/shared/store/user-vars';
import { MenuItem, MenuItemType, User } from '@nx-frontend/shared/types';
import { Member, NoteData } from '@nx-frontend/shared/types';
import {
  getBoxFileUrl,
  getLocaleDateString,
  getLocaleDateStringWithDiffTime,
  isValidUrl,
} from '@nx-frontend/shared/utils';
import { useOpenKitchenMenuContextFolder, useOpenKitchenMenuContextNote } from '@nx-frontend/widgets/sidebar';

export function NotesList({ items, showAuthor }: { items: NoteData[]; showAuthor: boolean }) {
  const router = useRouter();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [members, user, dateTimeLocale] = useUnit([$members, $user, $dateTimeLocale]);
  const openKitchenMenuContextFolder = useOpenKitchenMenuContextFolder();
  const openKitchenMenuContextNote = useOpenKitchenMenuContextNote();

  const dateLocale = typeof dateTimeLocale === 'string' ? dateTimeLocale : undefined;

  const handleMoreButtonClick = useCallback(
    (e: React.MouseEvent, menuItem: NoteData) => {
      e.preventDefault();
      if (menuItem) {
        if (menuItem.type === MenuItemType.NOTES_FOLDER) {
          openKitchenMenuContextFolder(menuItem as MenuItem, e, false);
        } else if (menuItem.type === MenuItemType.NOTE) {
          openKitchenMenuContextNote(menuItem as MenuItem, e, false);
        }
      }
    },
    [openKitchenMenuContextFolder, openKitchenMenuContextNote]
  );

  const handleItemClick = useCallback(
    (e: React.MouseEvent, item: NoteData) => {
      const isOpenInNewTab: boolean = e.metaKey || e.ctrlKey;
      const pageType = item.type === MenuItemType.NOTE ? 'page' : 'folder';
      const url = `/space/${item.workspaceId}/${pageType}/${item.globalId}`;
      if (isOpenInNewTab) {
        window.open(url, '_blank');
        return;
      }
      router.push(url);
    },
    [router]
  );

  function getItemIcon({ type, icon }: { type: MenuItemType; icon: string }) {
    if (icon === 'folder') {
      return IconsMap.FOLDER_FILL;
    }
    return IconsMap.DOCUMENT;
  }

  const getUserName = useMemo(
    () => (member: Member | undefined, currentUser: User) => {
      if (!member) return 'Deleted member';
      const fullName: string[] = [];
      if (member.user.firstname) fullName.push(member.user.firstname);
      if (member.user.lastname) fullName.push(member.user.lastname);
      const name = fullName.length ? fullName.join(' ') : member.user.email;
      return `${name} ${currentUser.id === member.userId ? ' (You)' : ''}`;
    },
    []
  );

  return items ? (
    items.map((page: NoteData, index: number) => {
      const diffTime = getLocaleDateStringWithDiffTime(page.updatedAt, dateLocale);
      const userName = getUserName(members.entities[page.userId], user);
      const avatar = members.entities[page.userId]?.avatar ? (
        <img
          className="rounded-[50px]"
          alt={userName}
          src={
            getBoxFileUrl(members.entities[page.userId]?.avatar?.storedFileUUID || '', {
              width: 32,
              height: 32,
            }) || ''
          }
        />
      ) : (
        <Icon icon={IconsMap.USER} className="rounded-[50px] text-neutral-800" />
      );

      let itemsCounterDiv = null;
      if (page.type === MenuItemType.NOTES_FOLDER) {
        const countItems = (page.childrenFoldersCount || 0) + (page.childrenNotesCount || 0);
        let itemsText = 'items';
        let itemsTextToDisplay = '';
        if (countItems === 1) {
          itemsText = 'item';
        }
        if (countItems === 0) {
          itemsTextToDisplay = 'Empty folder';
        } else {
          itemsTextToDisplay = `${countItems} ${itemsText}`;
        }
        itemsCounterDiv = (
          <div
            className={classNames({
              'text-neutral-400': true,
              'text-[10px]': true,
              'leading-[10px]': true,
              'ml-1': page.shared,
            })}
          >
            {itemsTextToDisplay}
          </div>
        );
      }

      return (
        <tr
          data-cy="item-card"
          key={index}
          className="table-row border-b-[1px] border-solid border-neutral-200 hover:bg-zinc-50"
        >
          <td
            data-cy={`list-item-${page.globalId}`}
            className="table-cell min-w-[340px] cursor-pointer"
            onClick={(e) => handleItemClick(e, page)}
          >
            <div className="flex items-center gap-4 p-4">
              <div className="p-1.5 items-center gap-2.5 rounded border-[1px] border-solid border-neutral-200">
                {isValidUrl(page.icon) ? (
                  <img src={page.icon} alt="" className="w-4 h-4" />
                ) : (
                  <Icon icon={getItemIcon({ type: page.type, icon: page.icon })} className="text-neutral-500 w-4 h-4" />
                )}
              </div>
              <div className="flex flex-col justify-center items-start gap-[2px]">
                <div data-cy="item-name" className="text-[13px] font-normal max-w-[334px] truncate">
                  {page.name}
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex gap-[4px] items-center">
                    {page.shared ? <Icon icon={IconsMap.SHARE} className="text-neutral-500 w-3 h-3" /> : null}
                    {false && <Icon icon={IconsMap.BOOKMARK} className="text-neutral-500 w-3 h-3" />}
                    {itemsCounterDiv}
                  </div>
                </div>
              </div>
            </div>
          </td>
          {page.userId && showAuthor ? (
            <td className="table-cell">
              <div className="flex items-center gap-4">
                <div className="flex p-2 items-center gap-2.5">
                  <span className="w-4 h-4">{avatar}</span>
                  <div data-cy="item-author" className="text-[13px] font-normal max-w-[130px] truncate">
                    {userName}
                  </div>
                </div>
              </div>
            </td>
          ) : null}
          <td className="table-cell">
            <div className="flex items-center gap-4">
              <div data-cy="item-updated-date" className="flex p-2 items-center gap-2.5 text-[13px] text-neutral-500">
                {diffTime}
              </div>
            </div>
          </td>
          <td className="table-cell">
            <div className="flex items-center gap-4">
              <div
                data-cy="item-created-date"
                className="flex p-2 items-center gap-2.5 text-[13px] text-neutral-500"
                suppressHydrationWarning={true}
              >
                {getLocaleDateString(page.createdAt, dateLocale)}
              </div>
            </div>
          </td>
          <td className="table-cell">
            <div className="p-2 items-center flex">
              <button data-cy="item-context-menu" ref={buttonRef} onClick={(e) => handleMoreButtonClick(e, page)}>
                <Icon icon={IconsMap.MORE_HORIZONTAL} className="text-neutral-500 w-4 h-4" />
              </button>
            </div>
          </td>
        </tr>
      );
    })
  ) : (
    <tr>
      <td colSpan={4}>Empty</td>
    </tr>
  );
}
